import { PaymentRequisiteToType } from '@/entities/paymentRequisit'

/**
 * Форматирует номер карты, добавляя пробелы после каждых 4-х цифр
 * @param value - Входная строка
 * @returns Отформатированная строка и очищенное значение
 */
export const formatCardNumber = (value: string): { formatted: string; cleaned: string } => {
  // Удаляем все нецифровые символы
  const cleaned = value.replace(/\D/g, '')

  // Ограничиваем до 16 цифр
  const limited = cleaned.substring(0, 16)

  // Добавляем пробелы после каждых 4-х цифр
  const formatted = limited.replace(/(\d{4})(?=\d)/g, '$1 ')

  return { formatted, cleaned: limited }
}

/**
 * Форматирует номер телефона в формате +7 XXX XXX-XX-XX
/**
 * Форматирует номер телефона в формате +7 XXX XXX-XX-XX
 * @param value - Входная строка
 * @returns Отформатированная строка и очищенное значение
 */
export const formatPhoneNumber = (value: string): { formatted: string; cleaned: string } => {
  // Удаляем все нецифровые символы
  const digits = value.replace(/\D/g, '')

  if (!digits) {
    return { formatted: '+', cleaned: '' }
  }

  let countryCode = ''
  let nationalNumber = ''

  // Определяем код страны
  if (digits.startsWith('8') && digits.length > 1) {
    // Российский формат с 8
    countryCode = '7'
    nationalNumber = digits.substring(1, 11) // берем до 10 цифр после 8
  } else if (digits.startsWith('7')) {
    // Россия/Казахстан
    countryCode = '7'
    nationalNumber = digits.substring(1, 11)
  } else if (digits.startsWith('992')) {
    // Таджикистан
    countryCode = '992'
    nationalNumber = digits.substring(3, 12) // 9 цифр после кода
  } else if (digits.startsWith('996')) {
    // Киргизия
    countryCode = '996'
    nationalNumber = digits.substring(3, 12) // 9 цифр после кода
  } else if (digits.startsWith('994')) {
    // Азербайджан
    countryCode = '994'
    nationalNumber = digits.substring(3, 12) // 9 цифр после кода
  } else if (digits.startsWith('995')) {
    // Грузия
    countryCode = '995'
    nationalNumber = digits.substring(3, 12) // 9 цифр после кода
  } else if (digits.startsWith('99')) {
    // Если начинается с 99, но еще не определили точный код - ждем больше цифр
    return { formatted: '+' + digits, cleaned: digits }
  } else if (digits.startsWith('9')) {
    // Если только одна цифра 9 - не определяем код страны пока
    return { formatted: '+' + digits, cleaned: digits }
  } else {
    // По умолчанию считаем что это российский номер без кода страны
    countryCode = '7'
    nationalNumber = digits.substring(0, 10)
  }

  // Форматируем в зависимости от кода страны
  let formatted = '+' + countryCode

  if (countryCode === '7') {
    // Российский/Казахский формат: +7 XXX XXX-XX-XX
    if (nationalNumber.length > 0) {
      formatted += ' ' + nationalNumber.substring(0, Math.min(3, nationalNumber.length))
    }
    if (nationalNumber.length > 3) {
      formatted += ' ' + nationalNumber.substring(3, Math.min(6, nationalNumber.length))
    }
    if (nationalNumber.length > 6) {
      formatted += '-' + nationalNumber.substring(6, Math.min(8, nationalNumber.length))
    }
    if (nationalNumber.length > 8) {
      formatted += '-' + nationalNumber.substring(8, 10)
    }
  } else {
    // Другие страны: +XXX XX XXX XXXX
    if (nationalNumber.length > 0) {
      formatted += ' ' + nationalNumber.substring(0, Math.min(2, nationalNumber.length))
    }
    if (nationalNumber.length > 2) {
      formatted += ' ' + nationalNumber.substring(2, Math.min(5, nationalNumber.length))
    }
    if (nationalNumber.length > 5) {
      formatted += ' ' + nationalNumber.substring(5, Math.min(9, nationalNumber.length))
    }
  }

  return {
    formatted,
    cleaned: countryCode + nationalNumber
  }
}

/**
 * Валидирует номер телефона
 */
export const validatePhoneNumber = (value: string) => {
  if (!value) {
    return 'Номер телефона обязателен'
  }

  const phoneRegex = /^(\+7|7|8)?[\s-]?\(?[9][0-9]{2}\)?[\s-]?[0-9]{3}[\s-]?[0-9]{2}[\s-]?[0-9]{2}$/
  return phoneRegex.test(value.replace(/\D/g, '')) || 'Введите корректный номер телефона'
}

/**
 * Валидирует номер карты
 */
export const validateCardNumber = (value: string) => {
  if (!value) {
    return 'Номер карты обязателен'
  }

  const cardRegex = /^[0-9]{16}$/
  return cardRegex.test(value.replace(/\s/g, '')) || 'Введите корректный номер карты (16 цифр)'
}

/**
 * Преобразует строковые поля в числовые
 * @param data - Объект с данными
 * @param fields - Массив ключей, которые нужно преобразовать в числа
 * @returns Объект с преобразованными полями
 */
export const convertToNumbers = <T extends Record<string, unknown>>(
  data: T,
  fields: (keyof T)[]
): T => {
  const result = { ...data }

  fields.forEach((field) => {
    const value = result[field]
    if (value !== undefined && value !== null && value !== '') {
      // Приводим к числу только если значение существует
      result[field] = Number(value) as T[keyof T]
    }
  })

  return result
}

/**
 * Форматирует номер банковского счета в формате XXXXX XXX X XXXX XXXXXXX
 * @param value - Входная строка
 * @returns Отформатированная строка и очищенное значение
 */
export const formatBankAccount = (value: string): { formatted: string; cleaned: string } => {
  // Удаляем все нецифровые символы
  const cleaned = value.replace(/\D/g, '')
  // Ограничиваем до 20 цифр (стандартная длина расчетного счета)
  const limited = cleaned.substring(0, 20)
  // Форматируем счет с пробелами для удобства чтения: XXXXX XXX X XXXX XXXXXXX
  const formatted = limited.replace(/(\d{5})(\d{3})(\d{1})(\d{4})(\d+)/g, '$1 $2 $3 $4 $5')
  return { formatted, cleaned: limited }
}

/**
 * Свич-кейс форматтера по типу реквизита
 * @param type - код типа реквизиты
 * @param value - реквизит
 * @return Отформатированная строка и очищенное значение
 */

export const formatRequisitToType = (type: PaymentRequisiteToType, value: string): string => {
  switch (type) {
    case PaymentRequisiteToType.CardNumber:
      return formatCardNumber(value).formatted
    case PaymentRequisiteToType.SbpPhoneNumber:
      return formatPhoneNumber(value).formatted
    case PaymentRequisiteToType.AccountNumber:
      return formatBankAccount(value).formatted
    default:
      return value
  }
}
